import { type Ref, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  type HtsMediaOfferDialogContent,
  HtsMediaClaimDialog,
} from "../HtsMediaClaimDialog";
import { getOfferHotelSearchPath } from "../offerHotelSearch";
import { DealTile } from "@/modules/dealTiles";
import type { OfferTileProperties } from "@/tracking/events/raw/OfferRawEvent";
import {
  type OfferScreen,
  ClaimStatus,
  OfferRawEventName,
  useEmitRbcRawEvent,
} from "@/tracking/offers";

export interface ClaimedOfferTileProps {
  /** Offer template plus the copy/image to show; also feeds the details dialog. */
  content: HtsMediaOfferDialogContent;
  imageSrc: string;
  description: string;
  trackingProperties: OfferTileProperties;
  screen: OfferScreen;
  rootRef?: Ref<HTMLDivElement>;
  className?: string;
}

/**
 * A loaded sponsored offer: "More info" opens the read-only offer dialog,
 * "Book" (or the card) starts a hotel search for the offer destination.
 */
export const ClaimedOfferTile = ({
  content,
  imageSrc,
  description,
  trackingProperties,
  screen,
  rootRef,
  className,
}: ClaimedOfferTileProps) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const emitTappedTile = useEmitRbcRawEvent(OfferRawEventName.TappedOfferTile);
  const { offerTemplate, assets } = content;

  const handleBookNow = () => {
    emitTappedTile({
      properties: {
        ...trackingProperties,
        screen,
        is_claimable: true,
        claim_status: ClaimStatus.already_claimed,
      },
    });
    navigate(getOfferHotelSearchPath(offerTemplate));
  };

  return (
    <>
      <DealTile
        rootRef={rootRef}
        className={className}
        imageSrc={imageSrc}
        title={content.title ?? assets.title}
        description={description}
        onMoreInfo={() => setDetailsOpen(true)}
        expiry={offerTemplate.expiry}
        status={t("dealTile.loaded")}
        action={{ label: t("dealTile.book"), onClick: handleBookNow }}
        onCardClick={handleBookNow}
      />
      <HtsMediaClaimDialog
        open={detailsOpen}
        content={content}
        ctaLabel={t("htsMedia.dialog.ctaDetails")}
        onCtaClick={() => setDetailsOpen(false)}
        onClose={() => setDetailsOpen(false)}
        screen={screen}
      />
    </>
  );
};
